import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import axios from '../../../../../http.service';
import * as XLSX from 'xlsx';
import moment from 'moment';
import FileSaver from 'file-saver';
import { exportHeaderOrder, headerKeyNames, validationLabels } from './pre-ex6.constants';
import { ADD_TOAST_MESSAGE } from '../../../../../Redux/Enums/Contracts.Globals.Enums';
import { UPDATE_REFINER_SELECTORS } from '../../../../../Redux/Enums/Contracts.Globals.Enums';

export const useFetchPreEx6MarkingTeams = (page, sizePerPage, setContracts, selectedYear, refiner, refinerFields, setRefinerContracts = []) => {
	const [isLoading, setIsLoading] = useState(true);
	const [total, setTotal] = useState(0);
    const qualification_year = selectedYear;
	const appointment_types = useSelector(state => state.contract_globals.appointment_types);
	const dispatch = useDispatch();
	
	useEffect(() => {
		fetchData(refiner && refiner.selectors);
    }, [page, sizePerPage, selectedYear]);

    function handleRefinementSelectorsChange(selectors) {
		if(!isLoading) 
		{
			// TODO [TL]: Figure out how to encapsulate this in the Refiner itself.
			// Stores the Refiner selector state within Redux for re-population later.
			dispatch({ type: UPDATE_REFINER_SELECTORS, payload: {selectors, refinerFields}});
			fetchData(selectors);
		}
	}

	const fetchData = async (selectors) => {

		try {
			setIsLoading(true);
			let query = {
				sortOrder: 'asc',
				sortField: 'title',
				page: page + 1,
				sizePerPage,
				searchText: ''
			};

			let title = '';
			let markingTeamCode = '';
			let markingMethod = '';
			let apptType = '';

			if (selectors && selectors.length > 0)
			{
				let titleField = selectors.find(e => e.field == 'title');
				if (titleField)
				{
					title = titleField.value;
				}

				let markingTeamCodeField = selectors.find(e => e.field == 'markingTeamCode');
				if (markingTeamCodeField)
				{
					markingTeamCode = markingTeamCodeField.value;
				}

				let markingMethodField = selectors.find(e => e.field == 'markingMethod');
				if (markingMethodField)
				{
					markingMethod = markingMethodField.value;
				}

				let apptTypeField = selectors.find(e => e.field == 'type');
				if (apptTypeField)
				{
					apptType = apptTypeField.value;
				}
			}

			let response = await axios.get(
				`/api/v2/release/preex6/${qualification_year}/${JSON.stringify(appointment_types.selected)}${
					query ? `?pagination=${JSON.stringify(query)}` : ''}&title=${title}&markingTeamCode=${markingTeamCode}&markingMethod=${markingMethod}&apptType=${apptType}`);	

			let {
				data: { contracts = [], total: responseTotal = 0 },
				errors = []
			} = response.data;

			// The contracts array is mutated when an appointee is chosen in the table dropdown. 
			// To prevent this, remove the properties amended for use in the Refiner in order for it to not fire uneccessary events.
			let refinerContracts = contracts.map(({appointmentId, ...item}) => item)
			setRefinerContracts(refinerContracts);
			setContracts(contracts);
			setTotal(responseTotal);

			if (errors.length > 0) {
				dispatch({ type: ADD_TOAST_MESSAGE, payload: { severity: 'error', message: 'An Error occurred when retrieving data' } });
			}
		} catch (error) {
			dispatch({ type: ADD_TOAST_MESSAGE, payload: { severity: 'error', message: error.message } });
		} finally {
			setIsLoading(false);
		}
	};
	
	return { isLoading, total, handleRefinementSelectorsChange };
};

export const getValidationLabels = ({ type }) => {
	let labels = validationLabels;
	labels.meetings_mar = type === 'CMK' ? 'Central Marker Meetings' : 'Marker Meetings';
	return labels;
};

export const useFormikValidateContract = row => {
	const dispatch = useDispatch();
	const labels = getValidationLabels(row);

	const formik = useFormik({
		initialValues: {
			contract_mgmt: row.isValidatedPreEx6,
			contract_fees: row.isValidatedPreEx6,
			spare_fields: row.isValidatedPreEx6,
			script_alloc: row.isValidatedPreEx6,
			script_dates: row.isValidatedPreEx6,
			meetings_mar: row.isValidatedPreEx6
		},
		validate: values => {
			const errors = {};
			Object.keys(values).forEach(key => {
				if (!values[key])
					Object.assign(errors, {
						[key]: `${labels[key]} is a required field`
					});
			});
			return errors;
		},
		onSubmit: async (values, actions) => {
			try {
				let data = {
					markingTeamId: row.markingTeamId,
					qualificationYear: row.qualificationYear,
					appointeeType: row.type,
					appointmentIds: row.appointees.map(a => a.apptId),
					...values
				};

				const response = await axios.put(`/api/v2/release/preex6/`, [data]);

				if (response.status === 200)
					dispatch({
						type: ADD_TOAST_MESSAGE,
						payload: {
							variant: 'success',
							message: 'Contract Status Ready to Issue'
						}
					});
				else
					dispatch({
						type: ADD_TOAST_MESSAGE,
						payload: {
							variant: 'error',
							message: 'An Error occured when updating Status'
						}
					});
			} catch (error) {
				dispatch({
					type: ADD_TOAST_MESSAGE,
					payload: {
						variant: 'error',
						message: error.message
					}
				});
			} finally {
				actions.setSubmitting(false);
			}
		}
	});
	return { formik, labels };
};

export const getExportData = async (markingTeamId, year, types) => {
	const { status, data } = await axios.get(`/api/v2/release/preex6/${markingTeamId}/${year}/${JSON.stringify(types)}`);

	return status === 200 ? data : [];
};

export const exportToCSV = async selected_row => {
	const csvData = await getExportData(selected_row.markingTeamId, selected_row.qualificationYear, [selected_row.type]);
	const writeSheet = XLSX.utils.json_to_sheet(csvData, {
		header: exportHeaderOrder
	});

	updateFieldNamesToDisplayName(writeSheet);

	const writeBuffer = {
		Sheets: { [`${selected_row.markingTeamCode}`]: writeSheet },
		SheetNames: [`${selected_row.markingTeamCode}`],
		Props: { Title: `${selected_row.title} ${selected_row.markingTeamCode}`, CreatedDate: moment() }
	};

	const buffer = XLSX.write(writeBuffer, {
		bookType: 'xlsx',
		type: 'array'
	});
	const data = new Blob([buffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
	});
	FileSaver.saveAs(data, `contract_ready_${selected_row.markingTeamCode}_${moment().format('DD/MM/YYYY HH:mm')}.xlsx`);

	return true;
};

const updateFieldNamesToDisplayName = writeSheet => {
	let range = XLSX.utils.decode_range(writeSheet['!ref']);
	for (let col = range.s.r; col <= range.e.c; ++col) {
		let address = XLSX.utils.encode_col(col) + '1';
		writeSheet[address].v = headerKeyNames[address];
	}
};
